import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import Sidebar from './Sidebar'; 
import config from '../config';

// Fix Leaflet icon issues
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

L.Marker.prototype.options.icon = DefaultIcon;

// Component to update map view when position changes
function SetViewOnLocationFound({ position }) {
  const map = useMap();
  
  useEffect(() => {
    if (position) {
      map.setView(position, 13);
    }
  }, [position, map]);
  
  return null;
}

const MapLayout = () => {
  const [position, setPosition] = useState([12.9716, 77.5946]); // Default to Bangalore
  const [selectedMarkerId, setSelectedMarkerId] = useState(null); 
  const [loading, setLoading] = useState(true);
  const [markers, setMarkers] = useState([]);
  const [error, setError] = useState(null);

  const customIcon = new L.Icon({
    iconUrl: '/pin.png',
    iconSize: [30, 30],
    iconAnchor: [15, 30],
    popupAnchor: [0, -30],
  });

  const handleMarkerClick = (id) => {
    setSelectedMarkerId(id);
  };

  const handleCloseSidebar = () => {
    setSelectedMarkerId(null);
  };

  // Add a new washroom marker
  const addNewWashroom = (washroomData) => {
    // Create a new marker with the form data
    const newMarker = {
      id: markers.length + 1,
      name: washroomData.name,
      description: washroomData.description,
      position: [washroomData.latitude, washroomData.longitude],
      imageUrl: URL.createObjectURL(washroomData.image), // Create a temporary URL for the image
      reviews: {},
      map: washroomData.googleMapsLink
    };

    // Add the new marker to the markers array
    setMarkers([...markers, newMarker]);

    // Here you would typically send the data to your backend API
    console.log('New washroom added:', newMarker);
    
    // Example of how you might send this to your backend:
    // const formData = new FormData();
    // formData.append('name', washroomData.name);
    // formData.append('description', washroomData.description);
    // formData.append('image', washroomData.image);
    // formData.append('latitude', washroomData.latitude);
    // formData.append('longitude', washroomData.longitude);
    // formData.append('map', washroomData.googleMapsLink);
    
    // fetch('https://findwashroom-production.up.railway.app/washrooms', {
    //   method: 'POST',
    //   body: formData
    // })
    // .then(response => response.json())
    // .then(data => {
    //   console.log('Success:', data);
    //   // Update your app state or UI as needed
    // })
    // .catch(error => {
    //   console.error('Error:', error);
    // });
  };

  const handleSubmitReview = async (markerId, reviewData) => {
    try {
      const marker = markers.find(m => m.id === markerId);
      const currentReviews = marker.reviews || {};
      const newReviewId = Object.keys(currentReviews).length + 1;

      console.log(`Submitting review for marker ${markerId}:`, reviewData);
      console.log(`Request URL: https://findwashroom-production.up.railway.app/washrooms/${markerId}/reviews`);
      console.log(`Request body:`, {
        reviewId: newReviewId,
        review: {
          user: "Anonymous",
          rating: reviewData.rating,
          comment: reviewData.comment,
          timestamp: new Date().toISOString()
        }
      });

      const response = await fetch(`https://findwashroom-production.up.railway.app/washrooms/${markerId}/reviews`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          reviewId: newReviewId,
          review: {
            user: "Anonymous",
            rating: reviewData.rating,
            comment: reviewData.comment,
            timestamp: new Date().toISOString()
          }
        }),
      });

      console.log(`Response status: ${response.status}`);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Error response: ${errorText}`);
        throw new Error(`Failed to submit review: ${response.status} ${errorText}`);
      }

      // Update local state to show the new review immediately
      setMarkers(currentMarkers => 
        currentMarkers.map(marker => {
          if (marker.id === markerId) {
            return {
              ...marker,
              reviews: {
                ...marker.reviews,
                [newReviewId]: {
                  user: "Anonymous",
                  rating: reviewData.rating,
                  comment: reviewData.comment,
                  timestamp: new Date().toISOString()
                }
              }
            };
          }
          return marker;
        })
      );
      //test

      alert('Review submitted successfully!');

    } catch (error) {
      console.error('Error submitting review:', error);
      alert('Failed to submit review. Please try again.');
    }
  };

  // Fetch washroom data from the backend
  useEffect(() => {
    fetch(`${config.apiUrl}/washrooms`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        // Filter out null values and format the data for markers
        const validMarkers = data.filter((item) => item !== null).map((item, index) => ({
          id: index + 1,
          name: item.name,
          map: item.map,
          description: item.description,
          imageUrl: `${index + 1}`,
          position: [item.location.lat, item.location.lng],
          reviews: item.reviews || {},
        }));
        console.log('Loaded washrooms:', validMarkers);
        setMarkers(validMarkers);
      })
      .catch((error) => {
        console.error('Error fetching washroom data:', error);
        setError('Failed to load washroom data. Please try again later.');
      });
  }, []);

  // Get user's current location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setPosition([latitude, longitude]);
          setLoading(false);
        },
        (error) => {
          console.error("Error getting location:", error);
          setError("Could not get your current location. Using default location instead.");
          setLoading(false);
        },
        { timeout: 10000, enableHighAccuracy: true }
      );
    } else {
      setError("Geolocation is not supported by your browser. Using default location.");
      setLoading(false);
    }
  }, []);

  if (loading) {
    return (
      <div className="loading-container">
        <p>Loading map and fetching your location...</p>
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="map-container">
      {error && <div className="error-message">{error}</div>}
      
      <MapContainer 
        center={position} 
        zoom={13} 
        style={{ height: '100%', width: '100%' }}
      >
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        
        {/* Component to update map view when position changes */}
        <SetViewOnLocationFound position={position} />
        
        {/* User's current location marker */}
        <Marker
          position={position}
          icon={DefaultIcon}
        >
          <Popup>Your current location</Popup>
        </Marker>
        
        {/* Washroom markers */}
        {markers.map(marker => (
          <Marker
            key={marker.id}
            position={marker.position}
            icon={customIcon}
            eventHandlers={{
              click: () => handleMarkerClick(marker.id),
            }}
          >
            <Popup>{marker.name}</Popup>
          </Marker>
        ))}
      </MapContainer>
      
      {/* Sidebar for selected washroom */}
      {selectedMarkerId && (
        <Sidebar 
          selectedMarkerId={selectedMarkerId} 
          markers={markers} 
          onClose={handleCloseSidebar}
          onSubmitReview={handleSubmitReview}
        />
      )}
    </div>
  );
};

export default MapLayout;
